<template>
    <div class="w-full bg-gray-50 p-4 md:p-6 rounded-xl">

        <modal name="modal-add-client" height="auto" :scrollable="true" :adaptive="true">
            <div class="p-6 overflow-y-auto max-h-[100vh]">
                <div class="flex items-center justify-between mb-4 pb-4 border-b">
                    <h2 class="text-xl font-bold">Fiche client</h2>
                    <button @click="$modal.hide('modal-add-client')" class="p-2"><i class="fas fa-times text-red-500"></i></button>
                </div>
                <client-form v-model="newClient" />
            </div>
        </modal>

        <modal name="modal-line-discount" height="auto" :scrollable="true" :adaptive="true">
            <div class="p-6 overflow-y-auto max-h-[100vh]">
                <h2 class="text-xl font-bold mb-4">Remise</h2>
                <div v-if="discountContext.line" class="space-y-4">
                    <div class="bg-gray-100 p-3 rounded-lg text-sm">
                        Sur : <strong>{{ discountContext.line.label }}</strong> ({{ formatMoney(lineTotalHt(discountContext.line)) }} € HT)
                    </div>
                    <select v-model="discountContext.mode" class="w-full border p-3 rounded-lg">
                        <option value="percent">Pourcentage (%)</option>
                        <option value="amount">Montant (€)</option>
                    </select>
                    <input type="number" v-model.number="discountContext.value" class="w-full border p-3 rounded-lg text-center font-bold" />
                </div>
                <div class="flex gap-3 mt-6">
                    <button @click="$modal.hide('modal-line-discount')" class="flex-1 py-3 border rounded-lg">Annuler</button>
                    <button @click="applyLineDiscount" class="flex-1 py-3 bg-emerald-600 text-white rounded-lg">Valider</button>
                </div>
            </div>
        </modal>

        <modal name="modal-presets" height="auto" :scrollable="true" :adaptive="true">
            <div class="p-6 overflow-y-auto max-h-[100vh]">
                <h2 class="text-xl font-bold mb-4">Texte rapide</h2>
                <div class="space-y-4">
                    <div v-for="(presets, category) in labelPresets" :key="category" v-show="presets.length > 0">
                        <h3 class="text-sm font-semibold text-gray-600 uppercase mb-2">
                            {{ getCategoryLabel(category) }}
                        </h3>
                        <div class="space-y-2">
                            <button v-for="preset in presets" :key="preset" @click="applyPresetTo(preset)" class="w-full text-left p-3 bg-gray-50 hover:bg-red-100 border border-gray-200 rounded-lg transition font-medium text-gray-700">
                                {{ preset }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </modal>


        <template v-if="loadingGetIntervention">
            <div class="py-12 text-center text-gray-500">
                <i class="fas fa-circle-notch fa-spin text-2xl"></i> Chargement...
            </div>
        </template>

        <template v-else>
            <div v-if="idIntervention" class="mb-4">
                <div>
                    <label class="block text-sm font-bold text-gray-700 mb-1">Document</label>
                    <div class="flex gap-3">
                        <button 
                            @click="documentType = 'facture'"
                            :class="[
                                'flex-1 py-2.5 px-4 rounded-lg font-medium transition',
                                documentType === 'facture' 
                                    ? 'bg-red-600 text-white shadow-md' 
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            ]"
                        >
                            <i class="fas fa-file-invoice mr-2"></i>Facture
                        </button>
                        <button 
                            @click="documentType = 'devis'"
                            :class="[
                                'flex-1 py-2.5 px-4 rounded-lg font-medium transition',
                                documentType === 'devis' 
                                    ? 'bg-red-600 text-white shadow-md' 
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            ]"
                        >
                            <i class="fas fa-file-alt mr-2"></i>Devis
                        </button>
                    </div>
                </div>
            </div>
            <div v-if="!idIntervention" class="bg-white p-4 md:p-6 rounded-xl shadow-sm border border-gray-200 mb-6">
                <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                        <label class="block text-sm font-bold text-gray-700 mb-1">Document</label>
                        <select v-model="documentType" class="w-full border-gray-300 border rounded-lg p-2.5 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-red-500 transition">
                            <option value="facture">Facture</option>
                            <option value="devis">Devis</option>
                        </select>
                    </div>

                    <div>
                        <label class="block text-sm font-bold text-gray-700 mb-1">Client</label>
                        <div class="flex gap-2">
                            <div class="relative flex-1">
                                <input type="text" 
                                    v-model="clientSearch" 
                                    placeholder="Nom du client..." 
                                    class="w-full border-gray-300 border rounded-lg p-2.5 pl-9 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-red-500 transition"
                                    @focus="showClientSuggestions = true"
                                    @input="searchClients"
                                    @blur="hideClientSuggestionsDelayed"
                                >
                                <i class="fas fa-search absolute left-3 top-3 text-gray-400"></i>

                                <div v-if="showClientSuggestions && filteredClients.length" class="absolute top-full left-0 w-full bg-white border shadow-xl rounded-lg mt-1 z-10 max-h-60 overflow-y-auto">
                                    <div v-for="c in filteredClients" :key="c.id" @mousedown.prevent="selectClient(c)" class="p-3 hover:bg-red-50 cursor-pointer border-b last:border-0">
                                        <div class="font-bold">{{ c.prenom }} {{ c.nom }}</div>
                                        <div class="text-xs text-gray-500">{{ c.email }}</div>
                                    </div>
                                </div>
                            </div>
                            <button @click="$modal.show('modal-add-client')" class="bg-red-100 text-red-700 px-3 rounded-lg hover:bg-red-200 transition">
                                <i class="fas fa-plus"></i>
                            </button>
                        </div>
                    </div>

                    <div>
                        <label class="block text-sm font-bold text-gray-700 mb-1">Type d'intervention</label>
                        <select v-model="selectedTypeId" @change="onChangeType" class="w-full border-gray-300 border rounded-lg p-2.5 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-red-500 transition">
                            <option value="">-- Vierge --</option>
                            <option v-for="t in types" :key="t.id" :value="t.id">{{ t.nom }}</option>
                        </select>
                    </div>
                </div>
            </div>

            <div v-if="documentType" class="mb-6">
                <label class="block text-sm font-bold text-gray-700 mb-1">Lignes</label>

                <div v-if="lines.length === 0" class="text-center py-10 bg-white border-2 border-dashed border-gray-300 rounded-xl mb-4">
                    <p class="text-gray-400 mb-2">Aucune ligne facturée</p>
                    <button @click="addEmptyLine" class="text-red-600 font-bold hover:underline">Ajouter une première ligne</button>
                </div>

                <div v-if="lines && lines.length > 0" class="md:hidden space-y-4">
                    <div v-for="(line, index) in lines" :key="'mob_'+line._uid" class="bg-white border rounded-xl p-4 shadow-sm relative">
                        <div class="absolute top-2 right-2 flex gap-1">
                            <button @click="duplicateLine(index)" class="w-8 h-8 rounded-full bg-gray-100 text-red-600 flex items-center justify-center"><i class="fas fa-copy text-xs"></i></button>
                            <button @click="deleteLine(index)" class="w-8 h-8 rounded-full bg-red-50 text-red-600 flex items-center justify-center"><i class="fas fa-trash text-xs"></i></button>
                        </div>

                        <div class="mb-3 pr-20">
                            <label class="text-xs text-gray-400 font-bold uppercase">Libellé</label>
                            <div class="flex gap-2 mt-1 relative">
                                <div class="relative flex-1">
                                    <input 
                                        v-model="line.label" 
                                        type="text" 
                                        @input="handleLabelInput(line, index)"
                                        @focus="focusedLineIndex = index"
                                        @blur="delayHideSuggestions"
                                        class="w-full border-b border-gray-200 focus:border-red-500 outline-none py-1" 
                                        placeholder="Description..."
                                    >
                                    
                                    <div v-if="focusedLineIndex === index && filteredLineSuggestions.length > 0" 
                                         class="absolute top-full left-0 w-full bg-white border border-gray-200 shadow-xl rounded-lg z-50 mt-1 max-h-48 overflow-y-auto">
                                        <div v-for="suggestion in filteredLineSuggestions" :key="suggestion"
                                             @mousedown.prevent="selectSuggestion(line, suggestion)"
                                             class="px-3 py-2 text-sm hover:bg-red-50 cursor-pointer border-b last:border-0 text-gray-700">
                                            {{ suggestion }}
                                        </div>
                                    </div>
                                </div>

                                <button @click="openPresetModal(index)" class="text-yellow-500"><i class="fas fa-lightbulb"></i></button>
                            </div>
                        </div>

                        <div class="md:grid grid-cols-3 gap-3 mb-3">
                            <div>
                                <label class="text-xs text-gray-400 font-bold uppercase">Qté</label>
                                <input type="number" v-model.number="line.quantity" class="w-full border rounded p-2 text-center mt-1">
                            </div>
                            <div>
                                <label class="text-xs text-gray-400 font-bold uppercase">PU HT</label>
                                <input type="number" v-model.number="line.unit_price" class="w-full border rounded p-2 text-center mt-1">
                            </div>
                            <div>
                                <label class="text-xs text-gray-400 font-bold uppercase">TVA</label>
                                <select v-model.number="line.tva" class="w-full border rounded p-2 text-center mt-1 bg-white">
                                    <option :value="5.5">5.5</option>
                                    <option :value="10">10</option>
                                    <option :value="20">20</option>
                                </select>
                            </div>
                        </div>

                        <div class="flex justify-between items-center pt-3 border-t">
                            <button v-if="!line.is_discount" @click="openDiscountModal(line, index)" class="text-sm text-red-600 font-medium">
                                <i class="fas fa-percent"></i> Remise
                            </button>
                            <div class="text-lg font-bold text-gray-800">{{ formatMoney(lineTotalHt(line)) }} € HT</div>
                        </div>
                    </div>

                    <button @click="addEmptyLine" class="w-full bg-gray-100 py-4 border-t border-gray-300 text-gray-500 font-bold hover:bg-red-50 transition flex items-center justify-center gap-2">
                        <i class="fas fa-plus"></i> Ajouter une ligne
                    </button>
                </div>


                <div v-if="lines && lines.length > 0" class="hidden md:block bg-white border border-gray-200 rounded-xl overflow-visible shadow-sm">
                    <table class="w-full text-sm text-left">
                        <thead class="bg-gray-50 text-gray-500 font-bold uppercase text-xs">
                            <tr>
                                <th class="p-4">Désignation</th>
                                <th class="p-4 w-24 text-center">Qté</th>
                                <th class="p-4 w-32 text-right">PU HT</th>
                                <th class="p-4 w-24 text-center">TVA</th>
                                <th class="p-4 w-32 text-right">Total HT</th>
                                <th class="p-4 w-20 text-center"></th>
                            </tr>
                        </thead>
                        <tbody class="divide-y divide-gray-100">
                            <tr v-for="(line, index) in lines" :key="'pc_'+line._uid" class="hover:bg-red-50 transition group">
                                <td class="p-3 align-top">
                                    <div class="flex items-center gap-2 relative">
                                        <div class="w-full relative">
                                            <input 
                                                v-model="line.label" 
                                                type="text" 
                                                @input="handleLabelInput(line, index)"
                                                @focus="focusedLineIndex = index"
                                                @blur="delayHideSuggestions"
                                                class="w-full bg-transparent border-none outline-none focus:ring-0 placeholder-gray-400" 
                                                placeholder="Saisir description..."
                                            >

                                            <div v-if="focusedLineIndex === index && filteredLineSuggestions.length > 0" 
                                                class="absolute top-full left-0 w-full min-w-[300px] bg-white border border-gray-200 shadow-xl rounded-lg z-50 mt-1 max-h-60 overflow-y-auto">
                                                <div v-for="suggestion in filteredLineSuggestions" :key="suggestion"
                                                    @mousedown.prevent="selectSuggestion(line, suggestion)"
                                                    class="px-4 py-2 hover:bg-red-50 cursor-pointer border-b last:border-0 text-gray-800">
                                                    {{ suggestion }}
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <button @click="openPresetModal(index)" class="opacity-0 group-hover:opacity-100 text-yellow-500 transition"><i class="fas fa-lightbulb"></i></button>
                                    </div>
                                </td>
                                <td class="p-2 align-top"><input type="number" v-model.number="line.quantity" class="w-full text-center bg-transparent border border-transparent hover:border-gray-200 rounded py-1"></td>
                                <td class="p-2 align-top"><input type="number" v-model.number="line.unit_price" class="w-full text-right bg-transparent border border-transparent hover:border-gray-200 rounded py-1"></td>
                                <td class="p-2 align-top">
                                    <select v-model.number="line.tva" class="w-full text-center bg-transparent border-none cursor-pointer">
                                        <option :value="5.5">5.5%</option>
                                        <option :value="10">10%</option>
                                        <option :value="20">20%</option>
                                    </select>
                                </td>
                                <td class="p-4 align-top text-right font-bold">{{ formatMoney(lineTotalHt(line)) }} €</td>
                                <td class="p-2 align-top text-center">
                                    <div class="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition">
                                        <button @click="openDiscountModal(line, index)" class="text-red-500"><i class="fas fa-percent"></i></button>
                                        <button @click="deleteLine(index)" class="text-red-500"><i class="fas fa-times"></i></button>
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td colspan="6">
                                    <button @click="addEmptyLine" class="w-full bg-gray-100 py-4 border-t border-gray-300 text-gray-500 font-bold hover:bg-red-50 transition flex items-center justify-center gap-2">
                                        <i class="fas fa-plus"></i> Ajouter une ligne
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

            </div>

            <div v-if="documentType" class="flex flex-col md:flex-row justify-end items-start md:items-center gap-6 mt-8 pt-6 border-t border-gray-200">
                <div class="w-full md:w-auto md:min-w-[300px] space-y-2 text-right">
                    <div class="flex justify-between md:justify-end gap-8 text-gray-600 text-sm font-light">
                        <span>Total HT</span>
                        <span class="font-medium">{{ formatMoney(totalHt) }} €</span>
                    </div>
                    <div class="flex justify-between md:justify-end gap-8 text-gray-600 text-sm font-light">
                        <span>TVA</span>
                        <span class="font-medium">{{ formatMoney(totalTva) }} €</span>
                    </div>
                    <div class="flex justify-between md:justify-end gap-8 font-bold text-gray-900 pt-2 border-t mt-2">
                        <span>Total TTC</span>
                        <span class="text-slate-900">{{ formatMoney(totalTtcAfterGlobal) }} €</span>
                    </div>
                </div>
                <div class="w-full md:w-auto">
                    <button :disabled="loadingSave" @click="emitInvoice" class="w-full md:w-auto px-8 py-4 bg-emerald-600 hover:bg-emerald-700 text-white text-lg font-bold rounded-xl shadow-lg hover:shadow-xl transition transform hover:-translate-y-0.5">
                        <template v-if="loadingSave">
                            <i class="fas fa-circle-notch fa-spin"></i>
                        </template>
                        <template v-if="!loadingSave">
                            <i class="fas fa-check mr-2"></i> Valider {{ documentType }}
                        </template>
                    </button>
                </div>
            </div>

        </template>
    </div>
</template>

<script>
import ClientForm from '../../Client/Components/ClientForm.vue';

export default {
    name: 'InvoiceForm',
    components: {
        ClientForm
    },
    props: {
        idIntervention: {
            required: true
        }
    },
    watch: {
        lines: {
            handler() {
                if (!this.isInitialized) return;

                if (this.isReloadingForced) {
                    this.originalLinesSnapshot = JSON.stringify(this.lines);
                    this.isReloadingForced = false;
                }
            },
            deep: true
        }
    },
    data() {
        return {
            types: [],
            selectedTypeId: '',
            selectedType: null,

            documentType: 'facture',

            lines: [],

            selectedClient: null,
            clients: [],
            clientsMeta: { current_page: 1, last_page: 1 },
            clientSearch: '',
            isLoadingClients: false,

            globalTvaRate: 10,
            globalDiscountType: 'none',
            globalDiscountValue: 0,

            discountContext: {
                line: null,
                index: null,
                mode: 'percent',
                value: 0
            },

            // --- AUTOCOMPLETE STATES ---
            focusedLineIndex: null,
            filteredLineSuggestions: [],
            // ---------------------------

            showJson: false,

            labelPresets: {
                labour: [
                    "Main d'œuvre",
                    "Main d'œuvre supplémentaire",
                    "Heure supplémentaire"
                ],
                supply: [
                    "Petites fournitures",
                    "Joint / Téflon / Visserie",
                    "Filtre / Cartouche",
                    "Silicone / Colle"
                ],
                material: [
                    "Chaudière",
                    "Pompe à chaleur",
                    "Climatisation Mono-Split",
                    "Climatisation Multi-Split",
                    "Thermostat d'ambiance"
                ],
                forfait: [
                    "Forfait déplacement",
                    "Forfait diagnostic",
                    "Forfait entretien"
                ],
                service: [
                    "Mise en service",
                    "Contrôle étanchéité",
                    "Entretien annuel",
                    "Nettoyage"
                ],
                discount: [
                    "Remise commerciale",
                    "Remise exceptionnelle"
                ],
                other: []
            },

            presetTargetIndex: null,
            presetList: [],

            newClient: {},
            showClientSuggestions: false,
            searchClientTimeout: null,

            presetSelectedType: "labour",

            isInitialized: false,
            originalLinesSnapshot: [],
            isReloadingForced: false,

            loadingGetIntervention: false,
            loadingSave: false
        }
    },
    computed: {
        totalHt() {
            return this.lines.reduce((sum, line) => sum + this.lineTotalHt(line), 0)
        },
        totalTva() {
            return this.lines.reduce((sum, line) => {
                const ht = this.lineTotalHt(line)
                return sum + ht * (Number(line.tva || 0) / 100)
            }, 0)
        },
        totalTtc() {
            return this.totalHt + this.totalTva
        },
        globalDiscountAmountHt() {
            if (this.globalDiscountType === 'none') return 0
            if (this.globalDiscountType === 'percent') {
                return (this.totalHt * (this.globalDiscountValue || 0)) / 100
            }
            if (this.globalDiscountType === 'amount') {
                return Math.min(this.globalDiscountValue || 0, this.totalHt)
            }
            return 0
        },
        globalDiscountAmountTtc() {
            if (this.totalHt <= 0) return 0
            const ratio = this.globalDiscountAmountHt / this.totalHt
            return this.totalTtc * ratio
        },
        totalTtcAfterGlobal() {
            return Math.max(this.totalTtc - this.globalDiscountAmountTtc, 0)
        },
        discountLineAmountHt() {
            if (!this.discountContext.line) return 0;

            const baseHt = this.lineTotalHt(this.discountContext.line);

            if (this.discountContext.mode === 'percent') {
                return (baseHt * (this.discountContext.value || 0)) / 100;
            }

            if (this.discountContext.mode === 'amount') {
                return Math.min(this.discountContext.value || 0, baseHt);
            }

            if (this.discountContext.mode === 'full') {
                return baseHt;
            }

            return 0;
        },
        invoiceJson() {
            return {
                document_type: this.documentType,
                client_id: this.selectedClient ? this.selectedClient.id : null,
                intervention_type_id: this.selectedType ? this.selectedType.id : null,
                tva_rate: this.globalTvaRate,
                global_discount: {
                    type: this.globalDiscountType,
                    value: this.globalDiscountValue,
                    amount_ht: Number(this.globalDiscountAmountHt.toFixed(2)),
                    amount_ttc: Number(this.globalDiscountAmountTtc.toFixed(2))
                },
                lines: this.lines.map(line => ({
                    label: line.label,
                    type: line.type,
                    quantity: Number(line.quantity || 0),
                    unit_price: Number(line.unit_price || 0),
                    tva: Number(line.tva || 0),
                    is_discount: !!line.is_discount,
                    parent_line_uid: line.parent_line_uid || null
                })),
                totals: {
                    total_ht: Number(this.totalHt.toFixed(2)),
                    total_tva: Number(this.totalTva.toFixed(2)),
                    total_ttc: Number(this.totalTtc.toFixed(2)),
                    total_ttc_after_global_discount: Number(this.totalTtcAfterGlobal.toFixed(2))
                }
            }
        },
        prettyJson() {
            return JSON.stringify(this.invoiceJson, null, 2)
        },

        filteredClients() {
            if (!this.clientSearch || this.clientSearch.length < 2) return []
            return this.clients
        },
        
        // --- NOUVEAU : Aplatit tous les presets en une seule liste pour la recherche
        flatPresets() {
            return Object.values(this.labelPresets).flat();
        }
    },
    methods: {
        // --- LOGIQUE AUTOCOMPLETE ---
        handleLabelInput(line, index) {
            this.focusedLineIndex = index;
            const search = (line.label || '').toLowerCase();
            
            if (!search || search.length < 1) {
                this.filteredLineSuggestions = [];
                return;
            }

            // Recherche dans tous les presets
            this.filteredLineSuggestions = this.flatPresets.filter(preset => 
                preset.toLowerCase().includes(search)
            ).slice(0, 6); // Limiter à 6 résultats
        },
        selectSuggestion(line, suggestion) {
            line.label = suggestion;
            this.filteredLineSuggestions = [];
            this.focusedLineIndex = null;
        },
        delayHideSuggestions() {
            setTimeout(() => {
                this.focusedLineIndex = null;
            }, 200);
        },
        // ---------------------------

        getCategoryLabel(key) {
             const map = {
                labour: "Main d'œuvre",
                supply: "Fournitures",
                material: "Matériel",
                forfait: "Forfaits",
                service: "Services",
                discount: "Remises"
             };
             return map[key] || "Autre";
        },

        formatMoney(value) {
            if (isNaN(value)) return '0,00'
            return Number(value).toFixed(2).replace('.', ',')
        },
        addEmptyLine() {
            this.lines.push({
                _uid: 'line_' + Date.now() + '_' + Math.random().toString(16).slice(2),
                label: '',
                type: 'labour',
                quantity: 1,
                unit_price: 0,
                tva: this.globalTvaRate || 10,
                is_discount: false,
                parent_line_uid: null
            })
        },
        duplicateLine(index) {
            const base = this.lines[index]
            if (!base) return
            const copy = Object.assign({}, base, { _uid: 'line_' + Date.now() + '_' + Math.random().toString(16).slice(2) })
            this.lines.splice(index + 1, 0, copy)
        },
        deleteLine(index) {
            this.lines.splice(index, 1)
        },
        lineTotalHt(line) {
            const qty = Number(line.quantity || 0)
            const pu = Number(line.unit_price || 0)
            return qty * pu
        },
        async loadTypes() {
            try {
                this.types = await this.$request.post('intervention-types/list', { societe_id: this.$societeId })
            } catch (e) {
                console.error(e)
            }
        },
        async onChangeType(event) {
            const newTypeId = event.target.value;

            if (!this.hasUnsavedLineChanges()) {
                await this.applyTypeChange(newTypeId);
                return;
            }

            const confirmed = confirm(
                "⚠️ Vous avez modifié les lignes de la facture.\n" +
                "Changer le type d'intervention va supprimer ces modifications.\n\n" +
                "Voulez-vous continuer ?"
            );

            if (!confirmed) {
                event.target.value = this.selectedTypeId;
                return;
            }

            await this.applyTypeChange(newTypeId);
        },
        async applyTypeChange(newTypeId) {
            this.selectedTypeId = newTypeId;

            if (!newTypeId) {
                this.selectedType = null;
                this.lines = [];
                this.originalLinesSnapshot = JSON.stringify([]);
                return;
            }

            const found = this.types.find(t => t.id === newTypeId);
            this.selectedType = found || null;

            this.isReloadingForced = true;

            await this.reloadTypeLines();
        },
        async reloadTypeLines() {
            if (!this.selectedTypeId) return
            try {
                const defaultLines = await this.$request.post('intervention-types/default-lines/list', { intervention_type_id: this.selectedTypeId })
                this.lines = (defaultLines || []).map(dl => ({
                    _uid: 'line_' + dl.id + '_' + Math.random().toString(16).slice(2),
                    label: dl.label,
                    type: dl.type || 'labour',
                    quantity: Number(dl.quantity || 1),
                    unit_price: Number(dl.unit_price || 0),
                    tva: Number(dl.tva || this.globalTvaRate || 10),
                    is_discount: false,
                    parent_line_uid: null
                }))
            } catch (e) {
                console.error(e)
            }
        },
        openDiscountModal(line, index) {
            this.discountContext.line = line
            this.discountContext.index = index
            this.discountContext.mode = 'percent'
            this.discountContext.value = 0
            this.$modal.show('modal-line-discount')
        },
        applyLineDiscount() {
            if (!this.discountContext.line) return
            const amountHt = this.discountLineAmountHt
            if (amountHt <= 0) {
                this.$toast && this.$toast.error && this.$toast.error('Montant de remise invalide.')
                return
            }

            const parent = this.discountContext.line
            const parentIndex = this.discountContext.index

            const discountLine = {
                _uid: 'line_discount_' + Date.now() + '_' + Math.random().toString(16).slice(2),
                label: (parent.label || 'Remise') + ' - remise',
                type: 'discount',
                quantity: 1,
                unit_price: -Number(amountHt.toFixed(2)),
                tva: Number(parent.tva || 0),
                is_discount: true,
                parent_line_uid: parent._uid || null
            }

            this.lines.splice(parentIndex + 1, 0, discountLine)
            this.$modal.hide('modal-line-discount')
        },
        async emitInvoice() {
            if (!this.selectedClient) {
                this.$toast && this.$toast.error && this.$toast.error('Sélectionnez un client avant de valider.')
                return
            }
            if (this.lines.length === 0) {
                this.$toast && this.$toast.error && this.$toast.error('Ajoutez au moins une ligne.')
                return
            }

            this.loadingSave = true
            let response = await this.$request.post('/factures/create', {
                json: JSON.stringify(this.invoiceJson),
                intervention_id: this.idIntervention || null
            })
            this.loadingSave = false

            if (response.status == 'error') {
                this.$toast.error('Erreur lors de la création de la facture.')
                return
            }
            
            this.$emit('submit', this.invoiceJson)
        },
        openPresetModal(index) {
            this.presetTargetIndex = index;

            const line = this.lines[index];
            this.presetSelectedType = line.type || "labour";

            this.updatePresetList();
            this.$modal.show('modal-presets');
        },
        updatePresetList() {
            this.presetList = this.labelPresets[this.presetSelectedType] || [];
        },
        applyPresetTo(text) {
            this.lines[this.presetTargetIndex].label = text;
            this.closePresetModal();
        },
        closePresetModal() {
            this.$modal.hide('modal-presets');
        },
        async searchClients() {
            clearTimeout(this.searchClientTimeout)

            this.searchClientTimeout = setTimeout(async () => {
                if (!this.clientSearch || this.clientSearch.length < 2) {
                    this.clients = []
                    return
                }

                try {
                    const res = await this.$request.get("/clients", {
                        search: this.clientSearch,
                        with_adresses: 1
                    })

                    this.clients = res.data || res
                } catch (e) {
                    console.error("Erreur recherche client", e)
                }
            }, 150)
        },
        selectClient(client) {
            this.selectedClient = client
            this.clientSearch = `${client.prenom} ${client.nom}`
            this.showClientSuggestions = false
        },
        hideClientSuggestionsDelayed() {
            setTimeout(() => {
                this.showClientSuggestions = false
            }, 200)
        },
        hasUnsavedLineChanges() {
             return false;
        },

        async loadInterventionData() {
            if (!this.idIntervention) return;

            this.loadingGetIntervention = true;

            try {
                let response = await this.$request.get('/interventions/' + this.idIntervention);

                this.selectedClient = response.client || null;
                if (this.selectedClient) {
                    this.clientSearch = `${this.selectedClient.prenom} ${this.selectedClient.nom}`;
                }
                if (response.intervention_type) {
                    this.selectedTypeId = response.intervention_type.id;
                    this.selectedType = response.intervention_type;
                }

                await this.reloadTypeLines();
                

            } catch (e) {
                console.error("Erreur chargement intervention", e);
            } finally {
                this.loadingGetIntervention = false;
            }
        }
    },

    mounted() {
        this.loadInterventionData()
    }
}
</script>